import { addDays, formatDateBR } from "./dates";
import { MAX_TITLE_CENTS, addMonthsAnchored } from "./finance";

// ===== Contratos e cobranças recorrentes (RF-SER-01, RF-SER-02) =====

export const CONTRACT_STATUSES = ["active", "inactive", "settled", "exempt", "evaluation"] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];
export const CONTRACT_PERIODICITIES = [
  "monthly",
  "bimonthly",
  "quarterly",
  "semiannual",
  "annual",
  "biennial",
  "triennial",
] as const;
export type ContractPeriodicity = (typeof CONTRACT_PERIODICITIES)[number];
export const PERIOD_PRINTS = ["none", "current_month", "previous_month"] as const;
export type PeriodPrint = (typeof PERIOD_PRINTS)[number];
/** Meses entre duas cobranças. */
export const PERIOD_MONTHS: Readonly<Record<ContractPeriodicity, number>> = {
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
  biennial: 24,
  triennial: 36,
};
/** Contratos tratados por rodada de "Gerar cobranças" (transação curta; o resto sai na rodada seguinte). */
export const MAX_CONTRACTS_PER_RUN = 300;
/** Competência mais antiga aceita (meses para trás) e mais adiantada (meses para a frente). */
export const MAX_CONTRACT_COMPETENCE_BACK = 12;
export const MAX_CONTRACT_COMPETENCE_AHEAD = 1;

const COMPETENCE_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
export const isCompetence = (s: string) => COMPETENCE_RE.test(s);
/** `AAAA-MM` da data `AAAA-MM-DD`. */
const competenceOf = (date: string) => date.slice(0, 7);

const index = (c: string) => {
  const [y, m] = c.split("-").map(Number) as [number, number];
  return y * 12 + (m - 1);
};
const pad = (n: number, w = 2) => String(n).padStart(w, "0");

export function addCompetence(c: string, months: number): string {
  const t = index(c) + months;
  const y = Math.floor(t / 12);
  return `${pad(y, 4)}-${pad(t - y * 12 + 1)}`;
}

/** Meses de `a` até `b` (negativo quando `b` vem antes). */
export const monthsBetween = (a: string, b: string) => index(b) - index(a);

/** `10/2026`. */
export const formatCompetence = (c: string) => `${c.slice(5, 7)}/${c.slice(0, 4)}`;

/** Problema da competência pedida (de 12 meses atrás até o mês seguinte a hoje) ou null. */
export function competenceWindowProblem(competence: string, today: string): string | null {
  if (!isCompetence(competence)) return "Competência inválida (use mês/ano)";
  const diff = monthsBetween(competenceOf(today), competence);
  if (diff > MAX_CONTRACT_COMPETENCE_AHEAD) return "Gere cobranças até o mês seguinte";
  if (diff < -MAX_CONTRACT_COMPETENCE_BACK)
    return `Gere cobranças de até ${MAX_CONTRACT_COMPETENCE_BACK} meses atrás`;
  return null;
}

/**
 * O contrato cobra na competência: a partir do mês de início (data base, senão a data do contrato), no passo da
 * periodicidade (bimestral = a cada 2 meses contados do mês de início).
 */
export function isContractDue(
  c: { periodicity: ContractPeriodicity; startDate: string },
  competence: string,
): boolean {
  const diff = monthsBetween(competenceOf(c.startDate), competence);
  return diff >= 0 && diff % PERIOD_MONTHS[c.periodicity] === 0;
}

/** Vencimento: mês da competência + 0 (corrente), 1 (seguinte) ou 2 meses, no dia escolhido (ou no último dia). */
export const contractDueDate = (competence: string, monthOffset: number, day: number) =>
  addMonthsAnchored(`${competence}-01`, monthOffset, day);

const lastDay = (c: string) => addDays(`${addCompetence(c, 1)}-01`, -1);

/** Período impresso na cobrança: nenhum, o mês da competência ou o mês anterior a ela. */
export function billingPeriod(print: PeriodPrint, competence: string): { from: string; to: string } | null {
  if (print === "none") return null;
  const c = print === "current_month" ? competence : addCompetence(competence, -1);
  return { from: `${c}-01`, to: lastDay(c) };
}

/** Histórico da conta a receber (até 255 caracteres). */
export function billingDescription(b: {
  number: number;
  description: string | null;
  competence: string;
  period: { from: string; to: string } | null;
}): string {
  const tail = [
    `competência ${formatCompetence(b.competence)}`,
    ...(b.period ? [`período ${formatDateBR(b.period.from)} a ${formatDateBR(b.period.to)}`] : []),
  ].join(" — ");
  const head = `Contrato nº ${b.number}`;
  const room = 255 - head.length - tail.length - 6;
  const desc = b.description?.trim() ? b.description.trim().slice(0, Math.max(0, room)) : "";
  return [head, ...(desc ? [desc] : []), tail].join(" — ");
}

/**
 * Valor da cobrança: o do contrato; com valor total previsto, só o que falta para atingi-lo (0 = nada a cobrar).
 * Aritmética em BigInt (somas de muitas cobranças).
 */
export function billingAmount(
  amountCents: number,
  expectedTotalCents: number | null,
  billedCents: number,
): number {
  if (expectedTotalCents === null) return amountCents;
  const rest = BigInt(expectedTotalCents) - BigInt(billedCents);
  if (rest <= 0n) return 0;
  return Number(rest < BigInt(amountCents) ? rest : BigInt(amountCents));
}

/** Regras do cadastro que o zod não vê sozinho; null = válido. */
export function contractProblem(c: {
  amountCents: number;
  expectedTotalCents: number | null;
  dueDay: number;
  dueMonthOffset: number;
  contractDate: string;
  baseDate: string | null;
}): string | null {
  if (!Number.isSafeInteger(c.amountCents) || c.amountCents <= 0 || c.amountCents > MAX_TITLE_CENTS)
    return "Valor do contrato inválido";
  if (c.expectedTotalCents !== null && c.expectedTotalCents < c.amountCents)
    return "O valor total previsto não pode ser menor que o valor do contrato";
  if (!Number.isInteger(c.dueDay) || c.dueDay < 1 || c.dueDay > 31)
    return "Informe o dia do vencimento (1 a 31)";
  if (![0, 1, 2].includes(c.dueMonthOffset)) return "Vencimento: mês corrente, seguinte ou em dois meses";
  if (c.baseDate !== null && c.baseDate < c.contractDate)
    return "A data base não pode ser anterior à data do contrato";
  return null;
}

/** Dia da geração automática no mês da competência (mês sem o dia = último dia). */
export const autoRunOn = (competence: string, day: number) => contractDueDate(competence, 0, day);

/** Próxima geração automática a partir de hoje: o dia deste mês, se ainda não passou; senão o do mês seguinte. */
export function nextAutoRunOn(day: number, today: string): string {
  const here = autoRunOn(competenceOf(today), day);
  return here >= today ? here : autoRunOn(addCompetence(competenceOf(today), 1), day);
}
